import React, { useState, useEffect } from 'react';
import axios from 'axios';
import Select from 'react-select';
import MDEditor from '@uiw/react-md-editor';
import StateCard from './StateCard';
import { groupCarriersByName } from '../utils/carrierHelpers';

/**
 * MemberEditor Component
 * Inline member editor - just updates parent state
 * Parent's Save button will handle actual persistence
 */
function MemberEditor({ 
  member, 
  onChange, 
  onClose, 
  states = [], 
  carriers = [],
  isNew = false,
  availableTags = []
}) {
  // Track whether the existing member already has a password set (for display only)
  const hasExistingPassword = !!(member && member.password);
  const selectedCampaign = localStorage.getItem('selectedCampaign') || 'medicare';
  const isAcaCampaign = selectedCampaign.toLowerCase().includes('aca') || selectedCampaign.toLowerCase().includes('obamacare') || selectedCampaign.toLowerCase().includes('marketplace');
  const isMedicareCampaign = !isAcaCampaign;

  const [formData, setFormData] = useState(member ? {
    ...member,
    password: '', // Always blank — admin must type a NEW password to change it
    phone: member.phone || '',
    internalPhone: member.internalPhone || '',
    consent: member.consent || '',
    nonFfmConsent: member.nonFfmConsent || '',
    soaConsent: member.soaConsent || '',
    renewableConsent: member.renewableConsent || '',
    healthSherpaMarketingLink: member.healthSherpaMarketingLink || '',
    dentalInsuranceLink: member.dentalInsuranceLink || '',
    lifeInsuranceLink: member.lifeInsuranceLink || '',
    autoInsuranceLink: member.autoInsuranceLink || '',
    debtReliefLink: member.debtReliefLink || '',
    creditServicesLink: member.creditServicesLink || '',
    mcaLink: member.mcaLink || ''
  } : {
    email: '',
    name: '',
    npn: '',
    phone: '',
    internalPhone: '',
    password: '',
    consent: '',
    nonFfmConsent: '',
    soaConsent: '',
    renewableConsent: '',
    healthSherpaMarketingLink: '',
    dentalInsuranceLink: '',
    lifeInsuranceLink: '',
    autoInsuranceLink: '',
    debtReliefLink: '',
    creditServicesLink: '',
    mcaLink: '',
    enabled: true,
    priority: 1,
    weightage: 1,
    dailyCap: 0,
    monthlyCap: 0,
    statesWithCarriers: {}
  });

  const [showAddState, setShowAddState] = useState(false);
  const [selectedState, setSelectedState] = useState(null);
  const [newStateCarriers, setNewStateCarriers] = useState([]);
  const [editingState, setEditingState] = useState(null);

  const updateStateCarriers = (state, carriers) => {
    const updatedFormData = {
      ...formData,
      statesWithCarriers: {
        ...formData.statesWithCarriers,
        [state]: carriers
      }
    };
    setFormData(updatedFormData);
    
    // ⭐ CRITICAL FIX: Immediately notify parent of state carrier changes
    // This ensures AgentForm's formData is updated in real-time
    if (onChange) {
      onChange(updatedFormData);
    }
  };

  const deleteState = (state) => {
    const newStates = { ...formData.statesWithCarriers };
    delete newStates[state];
    setFormData({
      ...formData,
      statesWithCarriers: newStates
    });
  };

  // ⭐ ACTIVE STATES — toggle a single state on/off for this member
  const toggleMemberStateActive = (state) => {
    const allStates = Object.keys(formData.statesWithCarriers);
    const current = formData.activeStates || allStates;
    const updated = current.includes(state)
      ? current.filter(s => s !== state)
      : [...current, state];
    const toSave = updated.length === allStates.length ? null : updated;
    setFormData(prev => ({ ...prev, activeStates: toSave }));
  };

  const addNewState = () => {
    if (selectedState && newStateCarriers.length > 0) {
      setFormData({
        ...formData,
        statesWithCarriers: {
          ...formData.statesWithCarriers,
          [selectedState.value]: newStateCarriers
        }
      });
      setSelectedState(null);
      setNewStateCarriers([]);
      setShowAddState(false);
    }
  };

  const stateOptions = states
    .filter(s => !formData.statesWithCarriers[s.code])
    .map(s => ({ value: s.code, label: `${s.name} (${s.code})` }));

  const groupedAllCarriers = groupCarriersByName(carriers);
  const _selCode = selectedState?.value || '';
  const _scMap = {AL:'alabama',AK:'alaska',AZ:'arizona',AR:'arkansas',CA:'california',CO:'colorado',CT:'connecticut',DE:'delaware',FL:'florida',GA:'georgia',HI:'hawaii',ID:'idaho',IL:'illinois',IN:'indiana',IA:'iowa',KS:'kansas',KY:'kentucky',LA:'louisiana',ME:'maine',MD:'maryland',MA:'massachusetts',MI:'michigan',MN:'minnesota',MS:'mississippi',MO:'missouri',MT:'montana',NE:'nebraska',NV:'nevada',NH:'new hampshire',NJ:'new jersey',NM:'new mexico',NY:'new york',NC:'north carolina',ND:'north dakota',OH:'ohio',OK:'oklahoma',OR:'oregon',PA:'pennsylvania',RI:'rhode island',SC:'south carolina',SD:'south dakota',TN:'tennessee',TX:'texas',UT:'utah',VT:'vermont',VA:'virginia',WA:'washington',WV:'west virginia',WI:'wisconsin',WY:'wyoming'};
  const _allSN = Object.values(_scMap);
  const _curSN = _scMap[_selCode] || '';
  const carrierOptions = groupedAllCarriers
    .filter(group => {
      if (_selCode && group.name) {
        const n = group.name.toLowerCase();
        for (const sn of _allSN) { if (sn !== _curSN && n.includes(sn)) return false; }
      }
      const allSelected = group.carrierIds.every(carrierId =>
        newStateCarriers.some(nc => nc.CarrierId === carrierId)
      );
      return !allSelected;
    })
    .map(group => ({
      value: group,
      label: `${group.name} (${group.carrierIds.length})`
    }));

  return (
    <div style={{
      background: 'white',
      padding: '24px',
      borderRadius: '12px',
      border: '2px solid #e9d5ff',
      marginBottom: '16px',
      position: 'relative'
    }}>
      {/* Close button */}
      <button
        type="button"
        onClick={() => {
          onChange(null); // Signal cancel
          onClose();
        }}
        style={{
          position: 'absolute',
          top: '16px',
          right: '16px',
          padding: '4px 12px',
          background: '#6b7280',
          color: 'white',
          border: 'none',
          borderRadius: '6px',
          cursor: 'pointer',
          fontSize: '12px',
          fontWeight: '600'
        }}
      >
        ✕ Cancel
      </button>

      {/* Save button */}
      <button
        type="button"
        onClick={() => {
          // Validate
          if (!formData.email || !formData.name || !formData.npn) {
            alert('Please fill in Email, Name, and NPN');
            return;
          }
          if (isNew && !formData.password) {
            alert('Password is required for new members');
            return;
          }
          
          onChange(formData); // Pass final data
          onClose();
        }}
        style={{
          position: 'absolute',
          top: '16px',
          right: '88px',
          padding: '4px 12px',
          background: '#7c3aed',
          color: 'white',
          border: 'none',
          borderRadius: '6px',
          cursor: 'pointer',
          fontSize: '12px',
          fontWeight: '600'
        }}
      >
        ✓ Save
      </button>

      <h3 style={{ marginTop: 0, color: '#7c3aed', marginBottom: '20px' }}>
        {isNew ? '➕ Add New Member' : '✏️ Edit Member'}
      </h3>

      {/* Basic Info */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px', marginBottom: '16px' }}>
        <div>
          <label style={{ display: 'block', marginBottom: '4px', fontWeight: '600', fontSize: '13px' }}>
            Email *
          </label>
          <input
            type="email"
            value={formData.email}
            onChange={(e) => setFormData({ ...formData, email: e.target.value })}
            style={{ width: '100%', padding: '8px', borderRadius: '6px', border: '1px solid #d1d5db' }}
          />
        </div>

        <div>
          <label style={{ display: 'block', marginBottom: '4px', fontWeight: '600', fontSize: '13px' }}>
            Name *
          </label>
          <input
            type="text"
            value={formData.name}
            onChange={(e) => setFormData({ ...formData, name: e.target.value })}
            style={{ width: '100%', padding: '8px', borderRadius: '6px', border: '1px solid #d1d5db' }}
          />
        </div>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px', marginBottom: '16px' }}>
        <div>
          <label style={{ display: 'block', marginBottom: '4px', fontWeight: '600', fontSize: '13px' }}>
            NPN *
          </label>
          <input
            type="text"
            value={formData.npn}
            onChange={(e) => setFormData({ ...formData, npn: e.target.value })}
            style={{ width: '100%', padding: '8px', borderRadius: '6px', border: '1px solid #d1d5db' }}
          />
        </div>

        <div>
          <label style={{ display: 'block', marginBottom: '4px', fontWeight: '600', fontSize: '13px' }}>
            {isNew ? 'Password *' : 'New Password'}
            {!isNew && <span style={{ fontWeight: 'normal', fontSize: '11px', marginLeft: '8px', color: '#6b7280' }}>
              Leave blank to keep current
            </span>}
          </label>
          {!isNew && hasExistingPassword && !formData.password && (
            <div style={{
              marginBottom: '6px',
              padding: '6px 10px',
              background: '#d1fae5',
              border: '1px solid #6ee7b7',
              borderRadius: '6px',
              fontSize: '12px',
              color: '#065f46',
              fontWeight: '600'
            }}>
              ✅ Password is currently set — type a new one below to change it
            </div>
          )}
          <input
            type="text"
            value={formData.password}
            onChange={(e) => setFormData({ ...formData, password: e.target.value })}
            placeholder={isNew ? 'Enter password' : 'Type new password to change...'}
            style={{ 
              width: '100%', 
              padding: '8px', 
              borderRadius: '6px', 
              border: `1px solid ${formData.password ? '#f59e0b' : '#d1d5db'}`,
              fontFamily: 'monospace',
              fontSize: '14px',
              background: formData.password ? '#fef3c7' : 'white'
            }}
          />
          {formData.password && (
            <div style={{ marginTop: '4px', fontSize: '11px', color: '#b45309', fontWeight: '600' }}>
              ⚠️ New password will be saved — make sure it's correct
            </div>
          )}
          {!isNew && !hasExistingPassword && !formData.password && (
            <div style={{ marginTop: '4px', fontSize: '11px', color: '#dc2626' }}>
              ⚠️ No password set — this member cannot log in until a password is added
            </div>
          )}
        </div>
      </div>

      {/* Phone Number + Internal Phone */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px', marginBottom: '16px' }}>
        <div>
          <label style={{ display: 'block', marginBottom: '4px', fontWeight: '600', fontSize: '13px' }}>
            Business Phone <span style={{ color: '#ef4444' }}>*</span>
          </label>
          <input
            type="tel"
            value={formData.phone || ''}
            onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
            placeholder="+1 (555) 000-0000"
            style={{ width: '100%', padding: '8px', borderRadius: '6px', border: '1px solid #d1d5db' }}
          />
          <div style={{ marginTop: 5, padding: '6px 8px', background: '#f0f9ff', border: '1px solid #bae6fd', borderRadius: 5, fontSize: 11, color: '#0369a1', lineHeight: 1.5 }}>
            📱 <strong>Business phone</strong> — shown to clients in consent forms and conversations.
          </div>
        </div>
        <div>
          <label style={{ display: 'block', marginBottom: '4px', fontWeight: '600', fontSize: '13px' }}>
            Personal Phone <span style={{ fontWeight: 'normal', color: '#6b7280', fontSize: 11 }}>(Optional)</span>
          </label>
          <input
            type="tel"
            value={formData.internalPhone || ''}
            onChange={(e) => setFormData({ ...formData, internalPhone: e.target.value })}
            placeholder="+1 (555) 000-0000"
            style={{ width: '100%', padding: '8px', borderRadius: '6px', border: '1px solid #d1d5db' }}
          />
          <div style={{ marginTop: 5, padding: '6px 8px', background: '#fefce8', border: '1px solid #fde047', borderRadius: 5, fontSize: 11, color: '#854d0e', lineHeight: 1.5 }}>
            🔒 <strong>Personal phone</strong> — used for login OTP and password reset only. Never shown to clients.
          </div>
        </div>
      </div>

      <div style={{ marginBottom: '16px' }}>
        <label style={{ display: 'block', marginBottom: '4px', fontWeight: '600', fontSize: '13px' }}>
          HealthSherpa Marketing Link (Optional)
        </label>
        <input
          type="url"
          value={formData.healthSherpaMarketingLink || ''}
          onChange={(e) => setFormData({ ...formData, healthSherpaMarketingLink: e.target.value })}
          placeholder="https://example.healthsherpa.com/member123"
          style={{ width: '100%', padding: '8px', borderRadius: '6px', border: '1px solid #d1d5db' }}
        />
        <small style={{ display: 'block', marginTop: '4px', color: '#6b7280', fontSize: '11px' }}>
          Enter the member's HealthSherpa marketing link if applicable
        </small>
      </div>

      {/* ── Optional Insurance Links ── */}
      <div style={{ marginBottom: 16, padding: '12px 14px', background: '#f0f9ff', border: '1px solid #bae6fd', borderRadius: 8 }}>
        <div style={{ fontSize: 13, fontWeight: 700, color: '#0369a1', marginBottom: 12 }}>📋 Optional Insurance Links (shown in lead app)</div>
        <div style={{ marginBottom: 10 }}>
          <label style={{ display: 'block', marginBottom: 4, fontWeight: 600, fontSize: 13 }}>Dental Insurance Link (Optional)</label>
          <input type="url" value={formData.dentalInsuranceLink || ''} onChange={(e) => setFormData({ ...formData, dentalInsuranceLink: e.target.value })} placeholder="https://dental-signup-link.com" style={{ width: '100%', padding: '8px', borderRadius: 6, border: '1px solid #d1d5db', boxSizing: 'border-box' }} />
        </div>
        <div style={{ marginBottom: 10 }}>
          <label style={{ display: 'block', marginBottom: 4, fontWeight: 600, fontSize: 13 }}>Life Insurance Link (Optional)</label>
          <input type="url" value={formData.lifeInsuranceLink || ''} onChange={(e) => setFormData({ ...formData, lifeInsuranceLink: e.target.value })} placeholder="https://life-insurance-link.com" style={{ width: '100%', padding: '8px', borderRadius: 6, border: '1px solid #d1d5db', boxSizing: 'border-box' }} />
        </div>
        <div>
          <label style={{ display: 'block', marginBottom: 4, fontWeight: 600, fontSize: 13 }}>Auto Insurance Link (Optional)</label>
          <input type="url" value={formData.autoInsuranceLink || ''} onChange={(e) => setFormData({ ...formData, autoInsuranceLink: e.target.value })} placeholder="https://auto-insurance-link.com" style={{ width: '100%', padding: '8px', borderRadius: 6, border: '1px solid #d1d5db', boxSizing: 'border-box' }} />
        </div>
      </div>

      {/* ── Optional Partner Services Links ── */}
      <div style={{ marginBottom: 16, padding: '12px 14px', background: '#fefce8', border: '1px solid #fde047', borderRadius: 8 }}>
        <div style={{ fontSize: 13, fontWeight: 700, color: '#854d0e', marginBottom: 12 }}>🤝 Optional Partner Services Links (shown in lead app)</div>
        <div style={{ marginBottom: 10 }}>
          <label style={{ display: 'block', marginBottom: 4, fontWeight: 600, fontSize: 13 }}>Debt Relief Link (Optional)</label>
          <input type="url" value={formData.debtReliefLink || ''} onChange={(e) => setFormData({ ...formData, debtReliefLink: e.target.value })} placeholder="https://debt-relief-link.com" style={{ width: '100%', padding: '8px', borderRadius: 6, border: '1px solid #d1d5db', boxSizing: 'border-box' }} />
        </div>
        <div style={{ marginBottom: 10 }}>
          <label style={{ display: 'block', marginBottom: 4, fontWeight: 600, fontSize: 13 }}>Credit Services Link (Optional)</label>
          <input type="url" value={formData.creditServicesLink || ''} onChange={(e) => setFormData({ ...formData, creditServicesLink: e.target.value })} placeholder="https://credit-services-link.com" style={{ width: '100%', padding: '8px', borderRadius: 6, border: '1px solid #d1d5db', boxSizing: 'border-box' }} />
        </div>
        <div>
          <label style={{ display: 'block', marginBottom: 4, fontWeight: 600, fontSize: 13 }}>MCA (Merchant Cash Advance) Link (Optional)</label>
          <input type="url" value={formData.mcaLink || ''} onChange={(e) => setFormData({ ...formData, mcaLink: e.target.value })} placeholder="https://mca-link.com" style={{ width: '100%', padding: '8px', borderRadius: 6, border: '1px solid #d1d5db', boxSizing: 'border-box' }} />
        </div>
      </div>

      {/* Lead Statistics - Read-only display for existing members */}
      {!isNew && member && (
        <div style={{
          marginBottom: '16px',
          padding: '16px',
          background: '#f0f9ff',
          borderRadius: '8px',
          border: '1px solid #bae6fd'
        }}>
          <h4 style={{ marginTop: 0, marginBottom: '12px', fontSize: '14px', fontWeight: '700', color: '#0369a1' }}>
            📊 Opportunity Statistics (Read-only)
          </h4>
          <div style={{
            display: 'grid',
            gridTemplateColumns: member.ghlFilterTag ? 'repeat(auto-fit, minmax(90px, 1fr))' : 'repeat(auto-fit, minmax(100px, 1fr))',
            gap: '12px',
            fontSize: '13px'
          }}>
            <div>
              <div style={{ color: '#6b7280', marginBottom: '4px', fontSize: '11px' }}>Today</div>
              <div style={{ fontWeight: '700', color: '#0891b2', fontSize: '18px' }}>
                {member.dailySent || 0}
              </div>
            </div>
            <div>
              <div style={{ color: '#6b7280', marginBottom: '4px', fontSize: '11px' }}>Yesterday</div>
              <div style={{ fontWeight: '700', color: '#0891b2', fontSize: '18px' }}>
                {member.yesterdaySent || 0}
              </div>
            </div>
            <div>
              <div style={{ color: '#6b7280', marginBottom: '4px', fontSize: '11px' }}>This Month</div>
              <div style={{ fontWeight: '700', color: '#0891b2', fontSize: '18px' }}>
                {member.monthlySent || 0}
              </div>
            </div>
            <div>
              <div style={{ color: '#6b7280', marginBottom: '4px', fontSize: '11px' }}>Total (DB)</div>
              <div style={{ fontWeight: '700', color: '#0891b2', fontSize: '18px' }}>
                {member.totalSent || 0}
              </div>
            </div>
            {member.ghlFilterTag && (
              <div style={{
                background: '#dbeafe',
                padding: '8px',
                borderRadius: '4px',
                border: '1px solid #7dd3fc'
              }}>
                <div style={{ color: '#0c4a6e', marginBottom: '4px', fontSize: '10px', fontWeight: '600', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                  GHL {member.ghlFilterTag}
                </div>
                <div style={{ fontWeight: '700', color: '#0369a1', fontSize: '18px' }}>
                  {member.ghlCount !== undefined ? member.ghlCount : '-'}
                </div>
                <div style={{ fontSize: '9px', color: '#10b981', marginTop: '2px', fontWeight: '500' }}>
                  ✓ DB-cached
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Consent - Markdown Editor */}
      <div style={{ marginBottom: '16px' }}>
        <label style={{ display: 'block', marginBottom: '8px', fontWeight: '600', fontSize: '13px' }}>
          Consent / Agreement (Markdown Supported)
        </label>
        <div data-color-mode="light">
          <MDEditor
            value={formData.consent || ''}
            onChange={(value) => setFormData({ ...formData, consent: value || '' })}
            height={200}
            preview="edit"
            style={{ borderRadius: '6px' }}
          />
        </div>
      </div>

      {/* Non-FFM Consent - ACA only */}
      {isAcaCampaign && (
        <div style={{ marginBottom: '16px' }}>
          <label style={{ display: 'block', marginBottom: '8px', fontWeight: '600', fontSize: '13px' }}>
            Non-FFM Consent (Markdown Supported) <span style={{ fontWeight: 'normal', color: '#6b7280' }}>— ACA Agents Only</span>
          </label>
          <div data-color-mode="light">
            <MDEditor
              value={formData.nonFfmConsent || ''}
              onChange={(value) => setFormData({ ...formData, nonFfmConsent: value || '' })}
              height={200}
              preview="edit"
              style={{ borderRadius: '6px' }}
            />
          </div>
        </div>
      )}

      {/* SOA Consent - Medicare only */}
      {isMedicareCampaign && (
        <div style={{ marginBottom: '16px' }}>
          <label style={{ display: 'block', marginBottom: '8px', fontWeight: '600', fontSize: '13px' }}>
            SOA Consent (Markdown Supported) <span style={{ fontWeight: 'normal', color: '#6b7280' }}>— Medicare Agents Only</span>
          </label>
          <div data-color-mode="light">
            <MDEditor
              value={formData.soaConsent || ''}
              onChange={(value) => setFormData({ ...formData, soaConsent: value || '' })}
              height={200}
              preview="edit"
              style={{ borderRadius: '6px' }}
            />
          </div>
        </div>
      )}

      <div style={{ marginBottom: '16px' }}>
        <label style={{ display: 'block', marginBottom: '8px', fontWeight: '600', fontSize: '13px' }}>
          Renewable Consent (Markdown Supported) <span style={{ fontWeight: 'normal', color: '#6b7280' }}>— Shown to returning leads eligible for re-enrollment</span>
        </label>
        <div data-color-mode="light">
          <MDEditor
            value={formData.renewableConsent || ''}
            onChange={(value) => setFormData({ ...formData, renewableConsent: value || '' })}
            height={200}
            preview="edit"
            style={{ borderRadius: '6px' }}
          />
        </div>
      </div>

      {/* Status */}
      <div style={{ marginBottom: '16px' }}>
        <label style={{ 
          display: 'inline-flex',
          alignItems: 'center',
          cursor: 'pointer'
        }}>
          <input
            type="checkbox"
            checked={formData.enabled}
            onChange={(e) => setFormData({ ...formData, enabled: e.target.checked })}
            style={{ display: 'none' }}
          />
          <div style={{
            width: '52px',
            height: '28px',
            background: formData.enabled ? '#10b981' : '#9ca3af',
            borderRadius: '14px',
            position: 'relative',
            transition: 'background 0.3s'
          }}>
            <div style={{
              width: '24px',
              height: '24px',
              background: 'white',
              borderRadius: '50%',
              position: 'absolute',
              top: '2px',
              left: formData.enabled ? '26px' : '2px',
              transition: 'left 0.3s'
            }} />
          </div>
          <span style={{ marginLeft: '8px', fontWeight: '600', fontSize: '14px' }}>
            {formData.enabled ? 'Active' : 'Inactive'}
          </span>
        </label>
      </div>

      {/* Distribution Config */}
      <div style={{
        background: '#f9fafb',
        padding: '16px',
        borderRadius: '8px',
        marginBottom: '16px'
      }}>
        <h4 style={{ marginTop: 0, fontSize: '14px', fontWeight: '700' }}>Distribution Settings</h4>
        
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px', marginBottom: '12px' }}>
          <div>
            <label style={{ display: 'block', marginBottom: '4px', fontSize: '12px', fontWeight: '600' }}>
              Priority
            </label>
            <input
              type="number"
              min="1"
              value={formData.priority}
              onChange={(e) => setFormData({ ...formData, priority: parseInt(e.target.value) || 1 })}
              style={{ width: '100%', padding: '6px', borderRadius: '4px', border: '1px solid #d1d5db' }}
            />
          </div>

          <div>
            <label style={{ display: 'block', marginBottom: '4px', fontSize: '12px', fontWeight: '600' }}>
              Weightage (1-10)
            </label>
            <input
              type="number"
              min="1"
              max="10"
              value={formData.weightage}
              onChange={(e) => setFormData({ ...formData, weightage: parseInt(e.target.value) || 1 })}
              style={{ width: '100%', padding: '6px', borderRadius: '4px', border: '1px solid #d1d5db' }}
            />
          </div>
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px', marginBottom: '12px' }}>
          <div>
            <label style={{ display: 'block', marginBottom: '4px', fontSize: '12px', fontWeight: '600' }}>
              Daily Cap
            </label>
            <input
              type="number"
              min="0"
              value={formData.dailyCap}
              onChange={(e) => setFormData({ ...formData, dailyCap: parseInt(e.target.value) || 0 })}
              style={{ width: '100%', padding: '6px', borderRadius: '4px', border: '1px solid #d1d5db' }}
            />
          </div>

          <div>
            <label style={{ display: 'block', marginBottom: '4px', fontSize: '12px', fontWeight: '600' }}>
              Monthly Cap
            </label>
            <input
              type="number"
              min="0"
              value={formData.monthlyCap}
              onChange={(e) => setFormData({ ...formData, monthlyCap: parseInt(e.target.value) || 0 })}
              style={{ width: '100%', padding: '6px', borderRadius: '4px', border: '1px solid #d1d5db' }}
            />
          </div>
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: '1fr', gap: '16px' }}>
          <div>
            <label style={{ display: 'block', marginBottom: '4px', fontSize: '12px', fontWeight: '600' }}>
              Total Send Limit (Auto-disables member when reached)
            </label>
            <input
              type="number"
              min="0"
              value={formData.totalSend || 0}
              onChange={(e) => setFormData({ ...formData, totalSend: parseInt(e.target.value) || 0 })}
              style={{ width: '100%', padding: '6px', borderRadius: '4px', border: '1px solid #d1d5db' }}
            />
            <small style={{ display: 'block', marginTop: '4px', color: '#6b7280', fontSize: '11px' }}>
              📌 When member's total sent reaches this limit, they will be automatically disabled (set to inactive). Set to 0 for unlimited.
            </small>
          </div>
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: '1fr', gap: '16px', marginTop: '12px' }}>
          <div>
            <label style={{ display: 'block', marginBottom: '4px', fontSize: '12px', fontWeight: '600' }}>
              GHL Filter Tag (Optional - for counting totalSend from GHL)
            </label>
            <Select
              value={formData.ghlFilterTag ? { value: formData.ghlFilterTag, label: formData.ghlFilterTag } : null}
              onChange={(selected) => setFormData({ ...formData, ghlFilterTag: selected ? selected.value : '' })}
              options={[
                { value: '', label: '-- None --' },
                ...availableTags.map(tag => ({ value: tag, label: tag }))
              ]}
              placeholder={availableTags.length > 0 ? "Select a tag..." : "No tags available"}
              isClearable
              isSearchable
              menuPortalTarget={document.body}
              styles={{
                control: (base) => ({ ...base, minHeight: '38px', fontSize: '13px' }),
                menu: (base) => ({ ...base, zIndex: 9999 })
              }}
            />
            <small style={{ display: 'block', marginTop: '4px', color: '#6b7280', fontSize: '11px' }}>
              📌 Optional: Select a GHL tag to count this member's totalSend from GHL. Uses parent group's GHL location. Falls back to system count if not set.
              {availableTags.length > 0 ? ` (${availableTags.length} tags available)` : ' (No tags available - set parent group GHL token first)'}
            </small>
          </div>
        </div>
      </div>

      {/* States & Carriers */}
      <div style={{ marginBottom: '16px' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
          <h4 style={{ margin: 0, fontSize: '14px', fontWeight: '700' }}>States & Carriers</h4>
          <button
            type="button"
            onClick={() => setShowAddState(!showAddState)}
            style={{
              padding: '6px 12px',
              background: '#7c3aed',
              color: 'white',
              border: 'none',
              borderRadius: '6px',
              cursor: 'pointer',
              fontSize: '12px',
              fontWeight: '600'
            }}
          >
            {showAddState ? '✕ Cancel' : '+ Add State'}
          </button>
        </div>

        {showAddState && (
          <div style={{ marginBottom: '12px', padding: '12px', background: '#faf5ff', borderRadius: '6px' }}>
            <Select
              options={stateOptions}
              value={selectedState}
              onChange={setSelectedState}
              placeholder="Select a state..."
              menuPortalTarget={document.body}
              styles={{
                control: (base) => ({ ...base, marginBottom: '8px' }),
                menuPortal: (base) => ({ ...base, zIndex: 9999 })
              }}
            />
            
            {selectedState && (
              <>
                <Select
                  isMulti
                  options={carrierOptions}
                  value={groupCarriersByName(newStateCarriers).map(group => ({
                    value: group,
                    label: `${group.name} (${group.carrierIds.length})`
                  }))}
                  onChange={(selected) => {
                    if (!selected || selected.length === 0) {
                      setNewStateCarriers([]);
                    } else {
                      const newCarriers = [];
                      selected.forEach(item => {
                        item.value.carrierIds.forEach(carrierId => {
                          const carrier = carriers.find(c => c.CarrierId === carrierId);
                          if (carrier) newCarriers.push(carrier);
                        });
                      });
                      setNewStateCarriers(newCarriers);
                    }
                  }}
                  placeholder="Select carriers..."
                  menuPortalTarget={document.body}
                  styles={{
                    control: (base) => ({ ...base, marginBottom: '8px' }),
                    menuPortal: (base) => ({ ...base, zIndex: 9999 })
                  }}
                />
                <button
                  type="button"
                  onClick={addNewState}
                  disabled={!selectedState || newStateCarriers.length === 0}
                  style={{
                    padding: '6px 12px',
                    background: '#10b981',
                    color: 'white',
                    border: 'none',
                    borderRadius: '6px',
                    cursor: 'pointer',
                    fontSize: '12px',
                    fontWeight: '600',
                    opacity: (!selectedState || newStateCarriers.length === 0) ? 0.5 : 1
                  }}
                >
                  ✓ Add {selectedState.value} with {newStateCarriers.length} carrier(s)
                </button>
              </>
            )}
          </div>
        )}

        <div>
          {Object.keys(formData.statesWithCarriers).length === 0 ? (
            <div style={{
              padding: '16px',
              background: '#f9fafb',
              borderRadius: '6px',
              textAlign: 'center',
              color: '#6b7280',
              fontSize: '13px'
            }}>
              No states added yet. Click "+ Add State" to begin.
            </div>
          ) : (
            Object.entries(formData.statesWithCarriers).map(([state, stateCarriers]) => {
              const isStateActive = !formData.activeStates || formData.activeStates.includes(state);
              return (
                <div key={state} style={{ opacity: isStateActive ? 1 : 0.55, transition: 'opacity 0.2s' }}>
                  {/* ⭐ Active/Paused toggle per state */}
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '4px' }}>
                    <label style={{ display: 'flex', alignItems: 'center', gap: '6px', cursor: 'pointer', userSelect: 'none', fontSize: '13px', fontWeight: '600', color: isStateActive ? '#059669' : '#dc2626' }}>
                      <input
                        type="checkbox"
                        checked={isStateActive}
                        onChange={() => toggleMemberStateActive(state)}
                        style={{ width: '15px', height: '15px', accentColor: '#059669', cursor: 'pointer' }}
                      />
                      {isStateActive ? '✅ Active' : '⏸ Paused'}
                    </label>
                    {!isStateActive && (
                      <span style={{ fontSize: '11px', color: '#6b7280', fontStyle: 'italic' }}>
                        Not receiving leads for this state
                      </span>
                    )}
                  </div>
                  <StateCard
                    stateCode={state}
                    carriers={stateCarriers}
                    allCarriers={carriers}
                    onUpdate={updateStateCarriers}
                    onDelete={deleteState}
                    isEditing={editingState === state}
                    setIsEditing={(editing) => setEditingState(editing ? state : null)}
                  />
                </div>
              );
            })
          )}
        </div>
      </div>
    </div>
  );
}

export default MemberEditor;
